import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Zap, Star, Crown, ArrowRight, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CursorGlow from '../components/CursorGlow';
import ScrollProgress from '../components/ScrollProgress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = [
    {
      name: 'Starter',
      icon: Zap,
      description: 'Küçük işletmeler ve startuplar için',
      monthlyPrice: '15.000',
      yearlyPrice: '150.000',
      color: 'from-blue-500 to-cyan-500',
      features: [
        { text: 'Temel web sitesi tasarımı', included: true },
        { text: 'Responsive tasarım', included: true },
        { text: '5 sayfa', included: true },
        { text: 'SEO optimizasyonu', included: true },
        { text: 'Google Analytics', included: true },
        { text: 'Sosyal medya entegrasyonu', included: true },
        { text: '1 ay ücretsiz destek', included: true },
        { text: 'Mobil uygulama', included: false },
        { text: 'E-ticaret', included: false },
        { text: 'Özel entegrasyonlar', included: false }
      ],
      cta: 'Başla',
      popular: false
    },
    {
      name: 'Professional',
      icon: Star,
      description: 'Büyüyen işletmeler için',
      monthlyPrice: '35.000',
      yearlyPrice: '350.000',
      color: 'from-purple-500 to-pink-500',
      features: [
        { text: 'Gelişmiş web sitesi tasarımı', included: true },
        { text: 'Responsive tasarım', included: true },
        { text: '15 sayfa', included: true },
        { text: 'İleri SEO optimizasyonu', included: true },
        { text: 'Google Analytics & Tag Manager', included: true },
        { text: 'Sosyal medya yönetimi', included: true },
        { text: '3 ay ücretsiz destek', included: true },
        { text: 'Temel mobil uygulama', included: true },
        { text: 'E-ticaret entegrasyonu', included: true },
        { text: 'Özel entegrasyonlar (2 adet)', included: true }
      ],
      cta: 'En Popüler',
      popular: true
    },
    {
      name: 'Enterprise',
      icon: Crown,
      description: 'Kurumsal çözümler',
      monthlyPrice: 'Özel',
      yearlyPrice: 'Özel',
      color: 'from-orange-500 to-red-500',
      features: [
        { text: 'Özel web & mobil uygulama', included: true },
        { text: 'Sınırsız sayfa', included: true },
        { text: 'Yapay zeka entegrasyonu', included: true },
        { text: 'Özel SEO stratejisi', included: true },
        { text: 'Gelişmiş analytics & raporlama', included: true },
        { text: 'Tam sosyal medya yönetimi', included: true },
        { text: '12 ay premium destek', included: true },
        { text: 'Native mobil uygulamalar', included: true },
        { text: 'Gelişmiş e-ticaret', included: true },
        { text: 'Sınırsız özel entegrasyon', included: true }
      ],
      cta: 'İletişime Geç',
      popular: false
    }
  ];

  const faqs = [
    {
      question: 'Fiyatlara KDV dahil mi?',
      answer: 'Hayır, gösterilen fiyatlar KDV hariçtir. Faturalama sırasında %20 KDV eklenecektir.'
    },
    {
      question: 'Ödeme seçenekleri nelerdir?',
      answer: 'Kredi kartı, banka havalesi ve taksitli ödeme seçeneklerimiz mevcuttur. Kurumsal müşterilerimiz için özel ödeme planları sunuyoruz.'
    },
    {
      question: 'Paket değişikliği yapabilir miyim?',
      answer: 'Evet, istediğiniz zaman paketinizi yükseltebilir veya düşürebilirsiniz. Fark tutarı bir sonraki faturanıza yansıtılır.'
    },
    {
      question: 'İptal politikanız nedir?',
      answer: 'Proje başlamadan önce iptal edebilirsiniz. Proje başladıktan sonra tamamlanan aşamalar için ücret alınır.'
    },
    {
      question: 'Destek hizmeti nedir?',
      answer: 'Destek hizmeti, teknik sorunlar, güncellemeler ve küçük değişiklikler için e-posta ve telefon desteğini kapsar.'
    },
    {
      question: 'Özel paket oluşturabilir miyim?',
      answer: 'Elbette! İhtiyaçlarınıza özel paket oluşturabiliriz. İletişime geçin, size özel teklif hazırlayalım.'
    }
  ];

  const addons = [
    { name: 'Ek Sayfa', price: '2.500', unit: 'sayfa' },
    { name: 'Blog Sistemi', price: '15.000', unit: 'tek seferlik' },
    { name: 'Çoklu Dil Desteği', price: '10.000', unit: 'dil başına' },
    { name: 'Canlı Chat Entegrasyonu', price: '5.000', unit: 'tek seferlik' },
    { name: 'Ödeme Sistemi Entegrasyonu', price: '8.000', unit: 'sistem başına' },
    { name: 'Özel API Geliştirme', price: '25.000', unit: 'başlangıç' }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-teal-500/20 rounded-full blur-3xl bottom-0 left-1/4 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Şeffaf
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Fiyatlandırma</span>
          </h1>

          <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto">
            İşinize uygun paketi seçin. Gizli ücret yok, tüm özellikler şeffaf.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-full p-2 mb-12">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-3 rounded-full font-semibold transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Aylık
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-3 rounded-full font-semibold transition-all ${
                billingCycle === 'yearly'
                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Yıllık
              <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
                %15 İndirim
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-slate-800/40 backdrop-blur-sm border rounded-2xl p-8 transition-all duration-500 hover:transform hover:-translate-y-2 ${
                  plan.popular
                    ? 'border-cyan-500/50 shadow-2xl shadow-cyan-500/20 scale-105'
                    : 'border-slate-700/50 hover:border-cyan-500/50'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      En Popüler
                    </span>
                  </div>
                )}

                {/* Icon */}
                <div className="relative w-16 h-16 mb-6">
                  <div className={`absolute inset-0 bg-gradient-to-br ${plan.color} rounded-xl flex items-center justify-center`}>
                    <plan.icon className="w-8 h-8 text-white" />
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-400 mb-6">{plan.description}</p>

                {/* Price */}
                <div className="mb-8">
                  {plan.monthlyPrice === 'Özel' ? (
                    <div className="text-4xl font-bold text-white">Özel Fiyat</div>
                  ) : (
                    <>
                      <div className="text-4xl font-bold text-white mb-1">
                        ₺{billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                      </div>
                      <div className="text-gray-400">
                        / {billingCycle === 'monthly' ? 'ay' : 'yıl'}
                      </div>
                    </>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      {feature.included ? (
                        <Check className="w-5 h-5 text-cyan-400 mr-3 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-5 h-5 text-gray-600 mr-3 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={feature.included ? 'text-gray-300' : 'text-gray-600'}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link to="/iletisim">
                  <Button
                    className={`w-full ${
                      plan.popular
                        ? 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white'
                        : 'bg-slate-700/50 hover:bg-slate-700 text-white'
                    }`}
                  >
                    {plan.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Ek <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Hizmetler</span>
            </h2>
            <p className="text-gray-400 text-lg">
              Paketinizi ihtiyaçlarınıza göre özelleştirin
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {addons.map((addon, index) => (
              <div
                key={index}
                className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/50 transition-all"
              >
                <h3 className="text-lg font-bold text-white mb-2">{addon.name}</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-cyan-400">₺{addon.price}</span>
                  <span className="text-gray-400 text-sm">/ {addon.unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 flex items-center justify-center gap-3">
              <HelpCircle className="w-8 h-8 text-cyan-400" />
              Sıkça Sorulan Sorular
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl px-6 data-[state=open]:border-cyan-500/50"
              >
                <AccordionTrigger className="text-white hover:text-cyan-400 text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Hala Emin Değil misiniz?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Size özel bir paket oluşturalım. Ücretsiz danışmanlık için iletişime geçin.
          </p>
          <Link to="/iletisim">
            <Button className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-8 py-6 text-lg">
              Ücretsiz Danışmanlık Al
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
